import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import {
    armWillChange,
    disarmWillChange,
    parseNumericDataAttr,
    prefersReducedMotion,
} from './_shared';

gsap.registerPlugin(ScrollTrigger);

const SCALE_END = 1;
const DEFAULT_PARALLAX_INTENSITY = 0.05;
const SCRUB_SMOOTHING = 0.5;
const SELECTOR = '[data-mda-gsap-parallax]';

function getScaleStart(element) {
    const intensity = parseNumericDataAttr(
        element,
        'mdaGsapParallax',
        DEFAULT_PARALLAX_INTENSITY
    );

    return 1 + intensity * 6;
}

function createParallaxTween(element) {
    armWillChange(element);

    gsap.set(element, {
        transformOrigin: '50% 50%',
    });

    return gsap.fromTo(
        element,
        { scale: getScaleStart(element) },
        {
            scale: SCALE_END,
            ease: 'none',
            scrollTrigger: {
                trigger: element,
                start: 'top bottom',
                end: 'bottom top',
                scrub: SCRUB_SMOOTHING,
                invalidateOnRefresh: true,
            },
        }
    );
}

function whenImageReady(element, callback) {
    if (element.complete && element.naturalHeight > 0) {
        callback();
        return null;
    }

    const handler = () => callback();

    element.addEventListener('load', handler, { once: true });

    return handler;
}

function createParallaxCoordinator(elements) {
    const images = elements.filter(element => element.tagName === 'IMG');

    if (!images.length) {
        return null;
    }

    const tweens = [];
    const pendingLoadHandlers = [];

    images.forEach(element => {
        const init = () => {
            tweens.push(createParallaxTween(element));
        };

        const loadHandler = whenImageReady(element, init);

        if (loadHandler) {
            pendingLoadHandlers.push({ element, loadHandler });
        }
    });

    return {
        elements: images,
        tweens,
        destroy() {
            pendingLoadHandlers.forEach(({ element, loadHandler }) => {
                element.removeEventListener('load', loadHandler);
            });

            tweens.forEach(tween => {
                tween.scrollTrigger?.kill();
                tween.kill();
            });

            disarmWillChange(images);
            images.forEach(element => {
                gsap.set(element, { clearProps: 'transform' });
            });
        },
    };
}

export class Parallax {
    static selector = SELECTOR;

    constructor(element, options = {}) {
        if (!element) {
            throw new Error('Parallax requires an element.');
        }

        this.element = element;
        this.options = options;
        this.coordinator = null;

        if (prefersReducedMotion()) {
            return;
        }

        this.coordinator = createParallaxCoordinator([element]);
    }

    destroy() {
        this.coordinator?.destroy();
        this.coordinator = null;
    }

    static init(root = document) {
        if (prefersReducedMotion() || !root?.querySelectorAll) {
            return [];
        }

        const elements = Array.from(root.querySelectorAll(SELECTOR));

        if (!elements.length) {
            return [];
        }

        const coordinator = createParallaxCoordinator(elements);

        return coordinator ? [coordinator] : [];
    }
}
